import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAdmin } from '../../contexts/AdminContext';
import { useSupabaseData } from '../../contexts/SupabaseDataContext';
import { storageApi, productApi } from '../../lib/supabase-api';
import RichTextEditor from '../common/RichTextEditor';

interface PriceEntry {
  quantity: number;
  original_price: number;
  sale_price: number;
  discount_rate: number;
}

interface FormData {
  category_id: number;
  name: string;
  prices: PriceEntry[]; // quantity-based price array
  manufacturer: string;
  description: string;
  ingredients: string;
  usage_instructions: string;
  warnings: string;
  storage_instructions: string;
  stock: number;
  image: string;
  rating: number;
  review_count: number;
}

const AdminProductForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { addProduct, updateProduct, getProductPrices, addProductPrice } = useAdmin();
  const { categories: flatCategories, isLoading: loading, error } = useSupabaseData();
  const isEditing = id && id !== 'new';
  
  const [formData, setFormData] = useState<FormData>({
    category_id: 0,
    name: '',
    prices: [
      { quantity: 100, original_price: 0, sale_price: 0, discount_rate: 0 },
      { quantity: 200, original_price: 0, sale_price: 0, discount_rate: 0 },
      { quantity: 300, original_price: 0, sale_price: 0, discount_rate: 0 },
      { quantity: 400, original_price: 0, sale_price: 0, discount_rate: 0 },
      { quantity: 500, original_price: 0, sale_price: 0, discount_rate: 0 }
    ],
    manufacturer: '',
    description: '',
    ingredients: '',
    usage_instructions: '',
    warnings: '',
    storage_instructions: '',
    stock: 0,
    image: '',
    rating: 0,
    review_count: 0
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Debug logging
  console.log('🔍 AdminProductForm - flatCategories:', flatCategories);
  console.log('🔍 AdminProductForm - loading:', loading);
  console.log('🔍 AdminProductForm - error:', error);
  
  // Using Supabase categories
  const categories = flatCategories.map(cat => ({ id: cat.id, name: cat.name }));
  console.log('🔍 AdminProductForm - mapped categories:', categories);

  // Quantity-based price helper functions
  const updatePriceEntry = (index: number, field: keyof PriceEntry, value: number) => {
    setFormData(prev => {
      const newPrices = [...prev.prices];
      const entry = { ...newPrices[index] };
      
      if (field === 'original_price' || field === 'discount_rate') {
        // Auto-calculate sale price when original price or discount rate changes
        if (field === 'original_price') {
          entry.original_price = value;
        } else {
          entry.discount_rate = Math.max(0, Math.min(90, value)); // 0-90% limit
        }
        entry.sale_price = Math.round(entry.original_price * (1 - entry.discount_rate / 100));
      } else {
        entry[field] = value;
      }
      
      newPrices[index] = entry;
      return { ...prev, prices: newPrices };
    });
  };

  // Load existing data in edit mode
  useEffect(() => {
    const loadProductData = async () => {
      if (isEditing && id) {
        // getById fetches full product (incl. description/ingredients) — getAll() excludes them.
        let product;
        try {
          product = await productApi.getById(id);
        } catch (err) {
          console.error('Failed to load product for editing:', err);
        }
        if (product) {
          // Load existing product_prices data
          const existingPrices = await getProductPrices(id);
          
          // Initialize with 5 default quantities (matching DB pattern)
          const defaultPrices: PriceEntry[] = [
            { quantity: 100, original_price: 0, sale_price: 0, discount_rate: 0 },
            { quantity: 200, original_price: 0, sale_price: 0, discount_rate: 0 },
            { quantity: 300, original_price: 0, sale_price: 0, discount_rate: 0 },
            { quantity: 400, original_price: 0, sale_price: 0, discount_rate: 0 },
            { quantity: 500, original_price: 0, sale_price: 0, discount_rate: 0 }
          ];
          
          // Map existing price data if available
          const mappedPrices = defaultPrices.map(defaultPrice => {
            const existingPrice = existingPrices.find(p => p.quantity === defaultPrice.quantity);
            if (existingPrice) {
              return {
                quantity: existingPrice.quantity,
                original_price: existingPrice.original_price,
                sale_price: existingPrice.sale_price,
                discount_rate: existingPrice.discount_rate
              };
            }
            return defaultPrice;
          });
          
          setFormData({
            category_id: product.category_id || 0,
            name: product.name || '',
            prices: mappedPrices,
            manufacturer: product.manufacturer || '',
            description: product.description || '',
            ingredients: product.ingredients || '',
            usage_instructions: product.usage_instructions || '',
            warnings: product.warnings || '',
            storage_instructions: product.storage_instructions || '',
            stock: product.stock || 0,
            image: product.image || '',
            rating: product.rating || 0,
            review_count: product.review_count || 0
          });
        } else {
          navigate('/admin/products');
        }
      }
    };
    
    loadProductData();
  }, [id, isEditing, getProductPrices, navigate]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.category_id) {
      newErrors.category_id = 'Please select a category.';
    }

    if (!formData.name.trim()) {
      newErrors.name = 'Please enter a product name.';
    } else if (formData.name.length > 100) {
      newErrors.name = 'Product name must be within 100 characters.';
    }

    // Price validation - check prices array
    const hasValidPrice = formData.prices.some(price => price.original_price > 0);
    if (!hasValidPrice) {
      newErrors.prices = 'Please enter a price for at least one quantity.';
    }

    // Validate each price option
    formData.prices.forEach((price, index) => {
      if (price.original_price > 0) {
        if (price.original_price > 10000000) {
          newErrors[`price_${index}`] = `${price.quantity} option price is too high.`;
        }
        if (price.sale_price > price.original_price) {
          newErrors[`price_${index}`] = `${price.quantity} option sale price exceeds original price.`;
        }
      }
    });

    // Discount validation removed (replaced by quantity-based pricing)

    if (!formData.manufacturer.trim()) {
      newErrors.manufacturer = 'Please enter a manufacturer.';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Please enter a product description.';
    } else if (formData.description.length < 10) {
      newErrors.description = 'Product description must be at least 10 characters.';
    }

    if (formData.stock < 0) {
      newErrors.stock = 'Stock must be 0 or more.';
    }

    if (formData.image && !storageApi.isValidImageUrl(formData.image)) {
      newErrors.image = 'Please enter a valid image URL.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle image file upload
  const handleImageUpload = async (file: File) => {
    if (!file) return;

    // Check file size (5MB limit)
    if (file.size > 5 * 1024 * 1024) {
      setErrors(prev => ({...prev, image: 'Image size must be 5MB or less.'}));
      return;
    }

    // Check file format
    if (!file.type.match(/^image\/(jpeg|jpg|png|gif|webp)$/i)) {
      setErrors(prev => ({...prev, image: 'Only JPG, PNG, GIF, WebP images can be uploaded.'}));
      return;
    }

    setIsUploading(true);
    
    try {
      const uploadedUrl = await storageApi.uploadImage(file, 'product-images', 'products');
      
      if (uploadedUrl) {
        setFormData(prev => ({...prev, image: uploadedUrl}));
        setErrors(prev => ({...prev, image: ''}));
        console.log('✅ Image upload successful:', uploadedUrl);
      } else {
        setErrors(prev => ({...prev, image: 'Image upload failed.'}));
      }
    } catch (error) {
      console.error('❌ Image upload error:', error);
      setErrors(prev => ({...prev, image: 'An error occurred during image upload.'}));
    } finally {
      setIsUploading(false);
    }
  };

  // Handle local path conversion
  const handleConvertLocalPath = () => {
    if (formData.image && formData.image.startsWith('/images/')) {
      const convertedUrl = storageApi.convertLocalPathToSupabaseUrl(formData.image);
      setFormData(prev => ({...prev, image: convertedUrl}));
    }
  };

  const handleInputChange = (field: keyof FormData, value: string | number | boolean) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear field error on input
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }

    // Real-time validation
    validateField(field, value);
  };

  const validateField = (field: keyof FormData, value: string | number | boolean) => {
    const newErrors = { ...errors };

    switch (field) {
      case 'name':
        if (typeof value === 'string') {
          if (!value.trim()) {
            newErrors.name = 'Please enter a product name.';
          } else if (value.length > 100) {
            newErrors.name = 'Product name must be within 100 characters.';
          } else {
            delete newErrors.name;
          }
        }
        break;

      // original_price and discount case removed (replaced by quantity-based pricing)

      case 'manufacturer':
        if (typeof value === 'string') {
          if (!value.trim()) {
            newErrors.manufacturer = 'Please enter a manufacturer.';
          } else {
            delete newErrors.manufacturer;
          }
        }
        break;

      case 'description':
        if (typeof value === 'string') {
          if (!value.trim()) {
            newErrors.description = 'Please enter a product description.';
          } else if (value.length < 10) {
            newErrors.description = 'Product description must be at least 10 characters.';
          } else {
            delete newErrors.description;
          }
        }
        break;

      case 'stock':
        if (typeof value === 'number') {
          if (value < 0) {
            newErrors.stock = 'Stock must be 0 or more.';
          } else {
            delete newErrors.stock;
          }
        }
        break;

      case 'image':
        if (typeof value === 'string' && value && !storageApi.isValidImageUrl(value)) {
          newErrors.image = 'Please enter a valid image URL.';
        } else {
          delete newErrors.image;
        }
        break;

      case 'category_id':
        if (typeof value === 'string') {
          const categoryId = parseInt(value);
          if (!categoryId) {
            newErrors.category_id = 'Please select a category.';
          } else {
            delete newErrors.category_id;
          }
        }
        break;
    }

    setErrors(newErrors);
  };


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Use first valid price as base price
      const firstValidPrice = formData.prices.find(p => p.original_price > 0);
      const basePrice = firstValidPrice?.sale_price || firstValidPrice?.original_price || 0;
      
      const productData = {
        name: formData.name.trim(),
        category_id: formData.category_id,
        price: basePrice, // base price (for AdminContext compatibility)
        manufacturer: formData.manufacturer.trim(),
        description: formData.description.trim(),
        ingredients: formData.ingredients.trim(),
        usage_instructions: formData.usage_instructions.trim(),
        warnings: formData.warnings.trim(),
        storage_instructions: formData.storage_instructions.trim(),
        stock: formData.stock,
        image: formData.image.trim() || '',
        rating: formData.rating,
        review_count: formData.review_count,
        is_active: true,
        pill_quantity: 1
      };

      if (isEditing && id) {
        await updateProduct(id, productData);
        
        // Update prices when editing existing product
        console.log('🚀 DEBUG: Now updating product prices...');
        try {
          // Update form prices array to product_prices table (UPSERT)
          const pricePromises = formData.prices.map(price => 
            addProductPrice(id, {
              quantity: price.quantity,
              original_price: price.original_price,
              sale_price: price.sale_price,
              discount_rate: price.discount_rate
            })
          );
          await Promise.all(pricePromises);
          console.log('✅ DEBUG: All price updates completed successfully');
        } catch (priceError) {
          console.error('❌ DEBUG: Price update failed:', priceError);
          // Continue even if price update fails since product update succeeded
        }
      } else {
        await addProduct(productData);
        
        // For new products, pricing needs separate handling
        // (handled automatically if AdminContext is properly implemented)
        console.log('New product registration complete');
      }
      
      // AdminContext functions return void, so assume success
      alert(isEditing ? 'Product has been successfully updated.' : 'Product has been successfully registered.');
      navigate('/admin/products');
    } catch (error) {
      console.error('Error saving product:', error);
      alert('An error occurred while saving. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDraft = () => {
    if (!formData.name.trim() && !formData.description.trim()) {
      alert('Please enter a product name or description.');
      return;
    }

    // Save to localStorage as draft
    const draftKey = `admin_product_draft_${id || 'new'}`;
    localStorage.setItem(draftKey, JSON.stringify(formData));
    alert('Draft saved successfully.');
  };

  const loadDraft = () => {
    const draftKey = `admin_product_draft_${id || 'new'}`;
    const saved = localStorage.getItem(draftKey);
    if (saved) {
      const draftData = JSON.parse(saved);
      setFormData(draftData);
      localStorage.removeItem(draftKey);
      alert('Draft loaded successfully.');
    } else {
      alert('No saved draft found.');
    }
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-US').format(price);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="sm:flex sm:items-center sm:justify-between">
        <div>
          <nav className="flex" aria-label="Breadcrumb">
            <ol className="flex items-center space-x-4">
              <li>
                <Link to="/admin/products" className="text-neutral-400 hover:text-neutral-500">
                  Product Management
                </Link>
              </li>
              <li>
                <svg className="flex-shrink-0 h-5 w-5 text-neutral-300" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                </svg>
              </li>
              <li>
                <span className="text-neutral-500">
                  {isEditing ? 'Edit Product' : 'Add New Product'}
                </span>
              </li>
            </ol>
          </nav>
          <h1 className="mt-2 text-2xl font-bold text-neutral-900">
            {isEditing ? 'Edit Product' : 'Add New Product'}
          </h1>
        </div>
        <div className="mt-4 sm:mt-0 flex space-x-3">
          <button
            type="button"
            onClick={loadDraft}
            className="inline-flex items-center px-4 py-2 border border-neutral-300 shadow-sm text-sm font-medium rounded-md text-neutral-700 bg-white hover:bg-neutral-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-forest-500"
          >
            Load Draft
          </button>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Form */}
          <div className="lg:col-span-2 space-y-6">
            {/* Basic Information */}
            <div className="bg-white shadow rounded-lg">
              <div className="px-6 py-4 border-b border-neutral-200">
                <h2 className="text-lg font-medium text-neutral-900">Basic Information</h2>
              </div>
              <div className="p-6 space-y-6">
                {/* Category */}
                <div>
                  <label htmlFor="category" className="block text-sm font-medium text-neutral-700">
                    Category <span className="text-red-500">*</span>
                  </label>
                  <select
                    id="category_id"
                    value={formData.category_id}
                    onChange={(e) => handleInputChange('category_id', e.target.value)}
                    disabled={loading}
                    className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-forest-500 focus:border-forest-500 ${
                      errors.category_id ? 'border-red-300' : 'border-neutral-300'
                    } ${loading ? 'bg-neutral-100 cursor-not-allowed' : ''}`}
                  >
                    <option value={0}>
                      {loading ? 'Loading categories...' : 'Select a category'}
                    </option>
                    {categories.map(category => (
                      <option key={category.id} value={category.id}>{category.name}</option>
                    ))}
                  </select>
                  {error && (
                    <p className="mt-1 text-sm text-red-600">Failed to load categories: {error}</p>
                  )}
                  {errors.category_id && (
                    <p className="mt-1 text-sm text-red-600">{errors.category_id}</p>
                  )}
                  {!loading && categories.length === 0 && !error && (
                    <p className="mt-1 text-sm text-yellow-600">No categories found. Please add a category first.</p>
                  )}
                </div>

                {/* Product Name */}
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-neutral-700">
                    Product Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-forest-500 focus:border-forest-500 ${
                      errors.name ? 'border-red-300' : 'border-neutral-300'
                    }`}
                    placeholder="Enter product name"
                    maxLength={100}
                  />
                  {errors.name && (
                    <p className="mt-1 text-sm text-red-600">{errors.name}</p>
                  )}
                  <p className="mt-1 text-sm text-neutral-500">
                    {formData.name.length}/100 chars
                  </p>
                </div>

                {/* Manufacturer */}
                <div>
                  <label htmlFor="manufacturer" className="block text-sm font-medium text-neutral-700">
                    Manufacturer <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="manufacturer"
                    value={formData.manufacturer}
                    onChange={(e) => handleInputChange('manufacturer', e.target.value)}
                    className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-forest-500 focus:border-forest-500 ${
                      errors.manufacturer ? 'border-red-300' : 'border-neutral-300'
                    }`}
                    placeholder="Enter manufacturer name"
                  />
                  {errors.manufacturer && (
                    <p className="mt-1 text-sm text-red-600">{errors.manufacturer}</p>
                  )}
                </div>

                {/* Description */}
                <RichTextEditor
                  label="Product Description"
                  value={formData.description}
                  onChange={(value) => handleInputChange('description', value)}
                  placeholder="Enter a detailed product description. Markdown syntax is supported."
                  required
                  error={errors.description}
                  maxLength={2000}
                />
              </div>
            </div>

            {/* Pricing & Inventory */}
            <div className="bg-white shadow rounded-lg">
              <div className="px-6 py-4 border-b border-neutral-200">
                <h2 className="text-lg font-medium text-neutral-900">Pricing & Inventory</h2>
              </div>
              <div className="p-6 space-y-6">
                {/* Quantity-based Pricing */}
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-sm font-medium text-neutral-700">
                      Quantity-based Pricing <span className="text-red-500">*</span>
                    </h3>
                    <p className="text-xs text-neutral-500">Please enter a price for at least one quantity</p>
                  </div>
                  
                  <div className="space-y-4">
                    {formData.prices.map((price, index) => {
                      const discountRate = price.original_price > 0 
                        ? Math.round(((price.original_price - price.sale_price) / price.original_price) * 100)
                        : 0;
                      
                      return (
                        <div key={index} className="grid grid-cols-1 md:grid-cols-5 gap-4 p-4 border rounded-lg bg-neutral-50">
                          {/* Quantity */}
                          <div>
                            <label className="block text-xs font-medium text-neutral-700 mb-1">
                              Quantity
                            </label>
                            <div className="flex items-center">
                              <span className="text-sm font-medium">{price.quantity}units</span>
                            </div>
                          </div>
                          
                          {/* Original Price */}
                          <div>
                            <label className="block text-xs font-medium text-neutral-700 mb-1">
                              Original Price
                            </label>
                            <div className="relative">
                              <input
                                type="number"
                                value={price.original_price || ''}
                                onChange={(e) => {
                                  const newPrice = parseInt(e.target.value) || 0;
                                  const updatedPrices = [...formData.prices];
                                  updatedPrices[index] = {
                                    ...updatedPrices[index],
                                    original_price: newPrice,
                                    sale_price: newPrice,
                                    discount_rate: 0
                                  };
                                  setFormData(prev => ({ ...prev, prices: updatedPrices }));
                                }}
                                className="w-full px-2 py-1 text-sm border rounded focus:outline-none focus:ring-1 focus:ring-forest-500"
                                placeholder="0"
                                min="0"
                              />
                            </div>
                          </div>
                          
                          {/* Sale Price */}
                          <div>
                            <label className="block text-xs font-medium text-neutral-700 mb-1">
                              Sale Price
                            </label>
                            <div className="relative">
                              <input
                                type="number"
                                value={price.sale_price || ''}
                                onChange={(e) => {
                                  const newSalePrice = parseInt(e.target.value) || 0;
                                  const updatedPrices = [...formData.prices];
                                  const discountRate = price.original_price > 0 
                                    ? Math.round(((price.original_price - newSalePrice) / price.original_price) * 100)
                                    : 0;
                                  
                                  updatedPrices[index] = {
                                    ...updatedPrices[index],
                                    sale_price: newSalePrice,
                                    discount_rate: Math.max(0, discountRate)
                                  };
                                  setFormData(prev => ({ ...prev, prices: updatedPrices }));
                                }}
                                className="w-full px-2 py-1 text-sm border rounded focus:outline-none focus:ring-1 focus:ring-forest-500"
                                placeholder="0"
                                min="0"
                                max={price.original_price || undefined}
                              />
                            </div>
                          </div>
                          
                          {/* Discount Rate */}
                          <div>
                            <label className="block text-xs font-medium text-neutral-700 mb-1">
                              Discount Rate
                            </label>
                            <div className="flex items-center">
                              <span className="text-sm font-medium text-forest-400">
                                {discountRate}%
                              </span>
                            </div>
                          </div>
                          
                          {/* Savings */}
                          <div>
                            <label className="block text-xs font-medium text-neutral-700 mb-1">
                              Savings
                            </label>
                            <div className="flex items-center">
                              <span className="text-xs text-neutral-600">
                                {price.original_price > price.sale_price 
                                  ? `${formatPrice(price.original_price - price.sale_price)}USD`
                                  : '-'
                                }
                              </span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  
                  {errors.prices && (
                    <p className="mt-1 text-sm text-red-600">{errors.prices}</p>
                  )}
                  
                  {/* Pricing Help */}
                  <div className="text-xs text-neutral-500 mt-2">
                    <p>• Entering an original price automatically sets the sale price to the same value.</p>
                    <p>• Changing the sale price auto-calculates the discount rate and savings.</p>
                    <p>• Leave unused quantities blank.</p>
                  </div>
                </div>

                {/* Stock */}
                <div>
                  <label htmlFor="stock" className="block text-sm font-medium text-neutral-700">
                    Stock Quantity
                  </label>
                  <input
                    type="number"
                    id="stock"
                    value={formData.stock || ''}
                    onChange={(e) => handleInputChange('stock', parseInt(e.target.value) || 0)}
                    className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-forest-500 focus:border-forest-500 ${
                      errors.stock ? 'border-red-300' : 'border-neutral-300'
                    }`}
                    placeholder="0"
                    min="0"
                  />
                  {errors.stock && (
                    <p className="mt-1 text-sm text-red-600">{errors.stock}</p>
                  )}
                </div>
              </div>
            </div>


            {/* Additional Info */}
            <div className="bg-white shadow rounded-lg">
              <div className="px-6 py-4 border-b border-neutral-200">
                <h2 className="text-lg font-medium text-neutral-900">Additional Information</h2>
              </div>
              <div className="p-6 space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Rating */}
                  <div>
                    <label htmlFor="rating" className="block text-sm font-medium text-neutral-700">
                      Rating (Initial)
                    </label>
                    <input
                      type="number"
                      id="rating"
                      value={formData.rating || ''}
                      onChange={(e) => handleInputChange('rating', parseFloat(e.target.value) || 0)}
                      className="mt-1 block w-full px-3 py-2 border border-neutral-300 rounded-md shadow-sm focus:outline-none focus:ring-forest-500 focus:border-forest-500"
                      placeholder="0.0"
                      min="0"
                      max="5"
                      step="0.1"
                    />
                  </div>

                  {/* Review Count */}
                  <div>
                    <label htmlFor="review_count" className="block text-sm font-medium text-neutral-700">
                      Review Count (Initial)
                    </label>
                    <input
                      type="number"
                      id="review_count"
                      value={formData.review_count || ''}
                      onChange={(e) => handleInputChange('review_count', parseInt(e.target.value) || 0)}
                      className="mt-1 block w-full px-3 py-2 border border-neutral-300 rounded-md shadow-sm focus:outline-none focus:ring-forest-500 focus:border-forest-500"
                      placeholder="0"
                      min="0"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Product Details */}
            <div className="bg-white shadow rounded-lg">
              <div className="px-6 py-4 border-b border-neutral-200">
                <h2 className="text-lg font-medium text-neutral-900">Product Details</h2>
                <p className="mt-1 text-sm text-neutral-500">
                  Enter information that users will see on the product detail page.
                </p>
              </div>
              <div className="p-6 space-y-6">
                {/* Key Ingredients */}
                <RichTextEditor
                  label="Key Ingredients"
                  value={formData.ingredients}
                  onChange={(value) => handleInputChange('ingredients', value)}
                  placeholder="Enter the main ingredients or components of the product."
                  maxLength={1000}
                />

                {/* Usage Instructions */}
                <RichTextEditor
                  label="Usage Instructions"
                  value={formData.usage_instructions}
                  onChange={(value) => handleInputChange('usage_instructions', value)}
                  placeholder="Enter proper dosage and usage instructions."
                  maxLength={1000}
                />

                {/* Warnings */}
                <RichTextEditor
                  label="Warnings"
                  value={formData.warnings}
                  onChange={(value) => handleInputChange('warnings', value)}
                  placeholder="Enter precautions, side effects, and contraindications."
                  maxLength={1000}
                />

                {/* Storage Instructions */}
                <RichTextEditor
                  label="Storage Instructions"
                  value={formData.storage_instructions}
                  onChange={(value) => handleInputChange('storage_instructions', value)}
                  placeholder="Enter storage methods and guidelines."
                  maxLength={500}
                />
              </div>
            </div>

            {/* Images */}
            <div className="bg-white shadow rounded-lg">
              <div className="px-6 py-4 border-b border-neutral-200">
                <h2 className="text-lg font-medium text-neutral-900">Product Image</h2>
              </div>
              <div className="p-6 space-y-4">
                {/* Image Upload Section */}
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-2">
                    Image Upload
                  </label>
                  <div className="flex items-center space-x-4">
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={isUploading}
                      className="px-4 py-2 bg-forest-400 text-white text-sm font-medium rounded-md hover:bg-forest-500 disabled:bg-neutral-400 disabled:cursor-not-allowed"
                    >
                      {isUploading ? 'Uploading...' : 'Choose File'}
                    </button>
                    <span className="text-sm text-neutral-500">
                      JPG, PNG, GIF, WebP (max 5MB)
                    </span>
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) handleImageUpload(file);
                    }}
                    className="hidden"
                  />
                </div>

                {/* Enter URL Directly */}
                <div>
                  <label htmlFor="image" className="block text-sm font-medium text-neutral-700">
                    Or enter image URL directly
                  </label>
                  <div className="mt-1 flex space-x-2">
                    <input
                      type="url"
                      id="image"
                      value={formData.image}
                      onChange={(e) => handleInputChange('image', e.target.value)}
                      className={`flex-1 px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-forest-500 focus:border-forest-500 ${
                        errors.image ? 'border-red-300' : 'border-neutral-300'
                      }`}
                      placeholder="https://example.com/image.jpg"
                    />
                    {formData.image && formData.image.startsWith('/images/') && (
                      <button
                        type="button"
                        onClick={handleConvertLocalPath}
                        className="px-3 py-2 bg-yellow-600 text-white text-sm font-medium rounded-md hover:bg-yellow-700"
                        title="Convert local path to Supabase URL"
                      >
                        Convert
                      </button>
                    )}
                  </div>
                  {errors.image && (
                    <p className="mt-1 text-sm text-red-600">{errors.image}</p>
                  )}
                  {formData.image && formData.image.startsWith('/images/') && (
                    <p className="mt-1 text-sm text-yellow-600">
                      ⚠️ Local path detected. Click the "Convert" button to convert to Supabase URL.
                    </p>
                  )}
                </div>

                {/* Image Preview */}
                {formData.image && storageApi.isValidImageUrl(formData.image) && (
                  <div className="mt-4">
                    <p className="text-sm font-medium text-neutral-700 mb-2">Preview</p>
                    <img
                      src={formData.image}
                      alt="Product Image Preview"
                      className="h-32 w-32 object-cover rounded-lg border border-neutral-200"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">

            {/* Actions */}
            <div className="bg-white shadow rounded-lg">
              <div className="px-6 py-4 border-b border-neutral-200">
                <h3 className="text-lg font-medium text-neutral-900">Actions</h3>
              </div>
              <div className="p-6 space-y-3">
                <button
                  type="button"
                  onClick={handleDraft}
                  className="w-full inline-flex justify-center items-center px-4 py-2 border border-neutral-300 shadow-sm text-sm font-medium rounded-md text-neutral-700 bg-white hover:bg-neutral-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-forest-500"
                >
                  Save Draft
                </button>
                <Link
                  to="/admin/products"
                  className="w-full inline-flex justify-center items-center px-4 py-2 border border-neutral-300 shadow-sm text-sm font-medium rounded-md text-neutral-700 bg-white hover:bg-neutral-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-forest-500"
                >
                  Cancel
                </Link>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full inline-flex justify-center items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-forest-400 hover:bg-forest-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-forest-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-3 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Saving...
                    </>
                  ) : (
                    isEditing ? 'Save Changes' : 'Submit'
                  )}
                </button>
              </div>
            </div>

            {/* Help */}
            <div className="bg-white shadow rounded-lg">
              <div className="px-6 py-4 border-b border-neutral-200">
                <h3 className="text-lg font-medium text-neutral-900">Help</h3>
              </div>
              <div className="p-6">
                <div className="space-y-4 text-sm text-neutral-600">
                  <div>
                    <h4 className="font-medium text-neutral-900">Pricing Tips</h4>
                    <ul className="mt-2 space-y-1">
                      <li>• Research competitor prices before setting</li>
                      <li>• Set discount rates appropriately</li>
                      <li>• Consider seasonal price adjustments</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-medium text-neutral-900">Inventory Management</h4>
                    <ul className="mt-2 space-y-1">
                      <li>• Maintain adequate safety stock</li>
                      <li>• Restock quickly when out of stock</li>
                      <li>• Expiration date management is important</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default AdminProductForm;